import React from 'react';
import { APP_VERSION } from '../constants/appVersion';
import { useSupabase } from './SupabaseProvider';

type PatchNoteRow = {
  version: string;
  published_at: string | null;
  created_at: string | null;
};

type AppVersionContextValue = {
  version: string;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
};

const AppVersionContext = React.createContext<AppVersionContextValue | undefined>(undefined);

function getTimestamp(value: string | null): number {
  if (!value) return Number.NEGATIVE_INFINITY;
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : Number.NEGATIVE_INFINITY;
}

function findLatestVersion(rows: PatchNoteRow[]): string {
  if (!rows.length) return APP_VERSION;

  const latest = [...rows]
    .sort((a, b) => {
      const timeA = getTimestamp(a.published_at ?? a.created_at);
      const timeB = getTimestamp(b.published_at ?? b.created_at);
      return timeB - timeA;
    })
    .shift();

  return latest?.version ?? APP_VERSION;
}

export const AppVersionProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const supabase = useSupabase();
  const [version, setVersion] = React.useState(APP_VERSION);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const loadLatestVersion = React.useCallback(
    async (showLoading: boolean) => {
      if (showLoading) {
        setIsLoading(true);
      }
      setError(null);
      try {
        const { data, error } = await supabase
          .from('patch_notes')
          .select('version, published_at, created_at')
          .order('created_at', { ascending: false });

        if (error) {
          throw error;
        }

        const rows = (data ?? []) as PatchNoteRow[];
        setVersion(findLatestVersion(rows));
      } catch (err) {
        // Die Versionsanzeige bleibt optional, damit Rollout- oder Netzprobleme keinen Frontend-Fehler erzeugen.
        console.warn('Fehler beim Laden der Patchnotes-Version:', err);
        setError(null);
        setVersion(APP_VERSION);
      } finally {
        if (showLoading) {
          setIsLoading(false);
        }
      }
    },
    [supabase]
  );

  React.useEffect(() => {
    void loadLatestVersion(true);
  }, [loadLatestVersion]);

  const refresh = React.useCallback(async () => {
    await loadLatestVersion(true);
  }, [loadLatestVersion]);

  const value = React.useMemo(
    () => ({
      version,
      isLoading,
      error,
      refresh,
    }),
    [error, isLoading, refresh, version]
  );

  return <AppVersionContext.Provider value={value}>{children}</AppVersionContext.Provider>;
};

export function useAppVersion(): AppVersionContextValue {
  const context = React.useContext(AppVersionContext);
  if (!context) {
    throw new Error('useAppVersion must be used within an AppVersionProvider');
  }
  return context;
}

